import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface Props {
  className?: string;
}

interface PropOption {
  id: string;
  name: string;
  count: number;
}

const PropsUsedFacet: React.FC<Props> = ({ className }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedProps = searchParams.get('props_used')?.split(',') || [];

  // Fetch all available props with their usage count
  const { data: props = [], isLoading } = useQuery<PropOption[]>({
    queryKey: ['props', 'facet'],
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc('get_props_with_usage_count')
        .order('name');

      if (error) throw error;
      return data;
    }
  });

  const handlePropChange = (propName: string) => {
    const newProps = selectedProps.includes(propName)
      ? selectedProps.filter(p => p !== propName)
      : [...selectedProps, propName];

    // Update URL params
    if (newProps.length > 0) {
      searchParams.set('props_used', newProps.join(','));
    } else {
      searchParams.delete('props_used');
    }
    setSearchParams(searchParams);
  };

  if (isLoading || props.length === 0) return null;

  return (
    <div className={className}>
      <h3 className="text-lg font-semibold mb-3">Props Used</h3>
      <div className="space-y-2 max-h-48 overflow-y-auto">
        {props.map(prop => (
          <label
            key={prop.id}
            className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
          >
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
              checked={selectedProps.includes(prop.name.toLowerCase().replace(/\s+/g, '_'))}
              onChange={() => handlePropChange(prop.name.toLowerCase().replace(/\s+/g, '_'))}
            />
            <span className="text-sm text-gray-700">{prop.name}</span>
            <span className="text-xs text-gray-500">({prop.count})</span>
          </label>
        ))}
      </div>
    </div>
  );
};

export default PropsUsedFacet; 